import type {
  ChannelDescriptor,
  ElementDescriptor,
  ElementValues,
  FixtureDescriptor,
  FixtureValues,
  LiveState,
  RGB,
  RigSnapshot,
} from './types';
import { channelKeyOf, distinctChannels } from './rig';

interface IndexedValue {
  index: number;
  value: number;
}

interface ElementPatch {
  index: number;
  channels?: IndexedValue[];
  colorDisplay?: RGB;
  clearColorDisplay?: boolean;
}

interface ElementRunPatch {
  first: number;
  count: number;
  channels?: number[];
  values?: string;
  colors?: string;
  clearColorDisplay?: boolean;
}

interface FixturePatch {
  fixtureIndex: number;
  channels?: IndexedValue[];
  colorDisplay?: RGB;
  clearColorDisplay?: boolean;
  elements?: ElementPatch[];
  elementRuns?: ElementRunPatch[];
}

export interface DeltaV1 {
  projection: 'delta-v1';
  rigRevision: string;
  baseRevision: number;
  stateRevision: number;
  header?: Partial<Omit<LiveState, 'fixtures' | 'rigRevision' | 'stateRevision'>>;
  fixtures?: FixturePatch[];
  ack?: boolean;
  resyncRequired?: boolean;
}

export interface IndexedElementState {
  index: number;
  values?: number[];
  colorDisplay?: RGB | null;
}

export interface IndexedFixtureState {
  fixtureIndex: number;
  values?: number[];
  colorDisplay?: RGB | null;
  elements?: IndexedElementState[];
}

export interface StateSyncChunk {
  projection: 'delta-v1';
  rigRevision: string;
  stateRevision: number;
  chunkIndex: number;
  chunkCount: number;
  header: Partial<Omit<LiveState, 'fixtures' | 'rigRevision' | 'stateRevision'>>;
  fixtures?: IndexedFixtureState[];
}

export type ReduceResult =
  | { kind: 'applied'; state: LiveState }
  | { kind: 'duplicate'; state: LiveState }
  | { kind: 'awaiting-delta'; state: LiveState }
  | { kind: 'resync'; reason: string };

function keysForChannels(channels?: ChannelDescriptor[], explicit?: string[]): string[] {
  if (explicit?.length) return explicit;
  return (channels ?? []).map(channelKeyOf);
}

/**
 * The fixture-level key order. A flat legacy `channels` list repeats each key once per element, while the
 * controller publishes each distinct key once (`fixtureChannels`), so the fixture level is de-duped here.
 * Element keys are positional on both sides and are not.
 */
function fixtureKeys(descriptor: FixtureDescriptor): string[] {
  return keysForChannels(distinctChannels(descriptor.representativeChannels ?? descriptor.channels), descriptor.channelKeys);
}

function elementDescriptor(descriptor: FixtureDescriptor, index: number): ElementDescriptor | undefined {
  return descriptor.elements?.find((element) => element.index === index) ?? descriptor.elements?.[index];
}

function elementKeys(descriptor: FixtureDescriptor, index: number): string[] {
  const element = elementDescriptor(descriptor, index);
  const keys = keysForChannels(element?.channels, element?.channelKeys);
  return keys.length ? keys : fixtureKeys(descriptor);
}

function decodeU16(value: string | undefined, expected: number): number[] {
  if (expected === 0) return [];
  if (!value) throw new Error('Dense element run is missing values.');
  const raw = Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
  if (raw.length !== expected * 2) throw new Error('Dense element run does not match its declared shape.');
  return Array.from({ length: expected }, (_, index) => ((raw[index * 2] << 8) | raw[index * 2 + 1]) / 65_535);
}

function applyIndexed(target: Record<string, number>, keys: string[], changes: IndexedValue[]): Record<string, number> {
  const next = { ...target };
  for (const change of changes) {
    const key = keys[change.index];
    if (key == null) throw new Error(`Channel index ${change.index} is outside the rig topology.`);
    next[key] = change.value;
  }
  return next;
}

function indexedValues(keys: string[], values: number[] | undefined, label: string): Record<string, number> {
  const source = values ?? [];
  if (source.length !== keys.length) throw new Error(`${label} has ${source.length} values for ${keys.length} channels.`);
  return Object.fromEntries(keys.map((key, index) => [key, source[index]]));
}

export function installStateSync(chunks: StateSyncChunk[], rig: RigSnapshot): LiveState {
  const first = chunks[0];
  if (!first) throw new Error('State sync contained no chunks.');
  if (first.projection !== 'delta-v1') throw new Error(`Unsupported projection ${first.projection}.`);
  if (first.rigRevision !== rig.rigVersionId) throw new Error('The state belongs to a different rig revision.');
  if (first.chunkCount < 1 || chunks.length !== first.chunkCount) {
    throw new Error(`State sync received ${chunks.length} of ${first.chunkCount} chunks.`);
  }
  const ordered = [...chunks].sort((left, right) => left.chunkIndex - right.chunkIndex);
  const fixturesByIndex = new Map<number, IndexedFixtureState>();
  for (let index = 0; index < ordered.length; index += 1) {
    const chunk = ordered[index];
    if (
      chunk.chunkIndex !== index || chunk.chunkCount !== first.chunkCount ||
      chunk.rigRevision !== first.rigRevision || chunk.stateRevision !== first.stateRevision
    ) throw new Error(`State sync chunk ${index} metadata changed.`);
    for (const fixture of chunk.fixtures ?? []) {
      const existing = fixturesByIndex.get(fixture.fixtureIndex);
      if (!existing) {
        fixturesByIndex.set(fixture.fixtureIndex, { ...fixture, elements: [...(fixture.elements ?? [])] });
        continue;
      }
      if (JSON.stringify(existing.values ?? []) !== JSON.stringify(fixture.values ?? [])) {
        throw new Error(`Split fixture ${fixture.fixtureIndex} changed between chunks.`);
      }
      const seen = new Set(existing.elements?.map((element) => element.index));
      for (const element of fixture.elements ?? []) {
        if (seen.has(element.index)) throw new Error(`Fixture ${fixture.fixtureIndex} repeats element ${element.index}.`);
        existing.elements?.push(element);
      }
    }
  }

  const fixtures: FixtureValues[] = rig.fixtures.map((_, fixtureIndex) => {
    const indexed = fixturesByIndex.get(fixtureIndex);
    if (!indexed) throw new Error(`Controllable fixture ${fixtureIndex} is absent from state sync.`);
    return installIndexedFixtureState(indexed, fixtureIndex, rig);
  });

  return {
    activeSource: first.header.activeSource ?? 'panel',
    activeSourceLabel: first.header.activeSourceLabel,
    blackout: first.header.blackout ?? false,
    outputRateHz: first.header.outputRateHz ?? 0,
    fixtures,
    transport: first.header.transport ?? null,
    effects: first.header.effects ?? [],
    scene: first.header.scene ?? null,
    remoteLive: first.header.remoteLive,
    stream: first.header.stream,
    control: first.header.control,
    rigRevision: first.rigRevision,
    stateRevision: first.stateRevision,
  };
}

/** Expand one complete authored-tree fixture node through the same topology decoder as delta-v1 joins. */
export function installIndexedFixtureState(
  indexed: IndexedFixtureState,
  fixtureIndex: number,
  rig: RigSnapshot,
): FixtureValues {
  const descriptor = rig.fixtures[fixtureIndex];
  if (!descriptor) throw new Error(`Fixture index ${fixtureIndex} is outside the rig topology.`);
  if (indexed.fixtureIndex !== fixtureIndex) throw new Error(`Fixture node ${indexed.fixtureIndex} is stored at index ${fixtureIndex}.`);
  const fixtureChannelKeys = fixtureKeys(descriptor);
  const fixtureValues = indexedValues(fixtureChannelKeys, indexed.values, `Fixture ${fixtureIndex}`);
  for (const record of indexed.elements ?? []) {
    if (!elementDescriptor(descriptor, record.index)) {
      throw new Error(`Fixture ${fixtureIndex} publishes element ${record.index} the rig does not describe.`);
    }
  }
  const elementRecords = new Map((indexed.elements ?? []).map((element) => [element.index, element]));
  const elements = (descriptor.elements ?? []).flatMap((element) => {
    const record = elementRecords.get(element.index);
    const keys = elementKeys(descriptor, element.index);
    // GDTF represents a conventional one-part fixture as one synthetic "Main" geometry even though the
    // controller correctly publishes its channels at fixture scope. The old baseline omitted that synthetic
    // element, so keep it omitted too; genuinely multi-element fixtures must still carry every element record.
    const fixtureScopedMain = !record
      && (descriptor.elements?.length ?? 0) === 1
      && keys.length === fixtureChannelKeys.length
      && keys.every((key, index) => key === fixtureChannelKeys[index]);
    if (!record && fixtureScopedMain) return [];
    if (!record) throw new Error(`Fixture ${fixtureIndex} is missing element ${element.index}.`);
    return [{
      index: element.index,
      name: element.name,
      values: indexedValues(keys, record.values, `Fixture ${fixtureIndex} element ${element.index}`),
      colorDisplay: record.colorDisplay ?? indexed.colorDisplay,
    }];
  });
  return {
    deviceId: descriptor.deviceId,
    name: descriptor.name,
    values: fixtureValues,
    colorDisplay: indexed.colorDisplay,
    elements: elements.length > 0 ? elements : undefined,
  };
}

export type StateSyncRequest = (params: Record<string, unknown>) => Promise<StateSyncChunk | LiveState>;

/**
 * One restart. A revision that ages out twice while being read is a room changing faster than it can be
 * read at all, and a third attempt would only keep the console blank for longer.
 */
const STATE_SYNC_ATTEMPTS = 2;

function isStateSyncChunk(value: StateSyncChunk | LiveState): value is StateSyncChunk {
  return 'projection' in value && (value as StateSyncChunk).projection === 'delta-v1' && 'chunkCount' in value;
}

/**
 * Read a controller's whole state, over any transport, the one way that leaves a revision baseline behind.
 *
 * `installStateSync` is the only function in this file that writes `stateRevision`, and it only accepts the
 * chunked `delta-v1` projection — so asking for state any other way installs a room the console cannot
 * order against anything. That is not a cosmetic difference: `reduceStateDelta` answers `resync` to every
 * delta when the baseline is missing, each resync asks for state again, and if THAT answer is unrevisioned
 * too the loop never converges. The hosted console spent 2026-09-15 in exactly that loop — it sent
 * `get_state {}`, the controller answered with its unrevisioned compatibility snapshot (262 KB against the
 * projection's 91 KB on the same rig), and not one of the daemon's per-element deltas was ever applied.
 *
 * This is the one reader for LAN, Bluetooth, and the explicit 1.7.19 cloud compatibility join. The 1.7.20
 * cloud path observes complete authored tree nodes and never enters this function.
 *
 * A controller too old to understand `projection` still answers with a whole `LiveState`. That is accepted
 * and normalized, unrevisioned, because the alternative is a blank room — but it is the compatibility path,
 * not the design.
 */
export async function readStateSync(request: StateSyncRequest, rig: RigSnapshot | undefined): Promise<LiveState> {
  for (let attempt = 1; ; attempt += 1) {
    const first = await request({ projection: 'delta-v1', chunkIndex: 0 });
    if (!isStateSyncChunk(first)) return normalizeFullState(first, rig);
    if (!rig) throw new Error('The controller returned state before its rig was available.');
    let rest: StateSyncChunk[];
    try {
      rest = await Promise.all(
        Array.from({ length: Math.max(0, first.chunkCount - 1) }, (_, offset) => request({
          projection: 'delta-v1',
          stateRevision: first.stateRevision,
          chunkIndex: offset + 1,
        }) as Promise<StateSyncChunk>),
      );
    } catch (error) {
      // The room moved while we were reading it, so the revision chunk 0 named is gone and the controller
      // refuses the rest of it ("state revision N is no longer available; restart at chunk 0"). A newer
      // room is not a failure — and surfacing it as one would leave the console with no baseline, which is
      // the state that makes every later delta resync. Start again from chunk 0.
      if (attempt >= STATE_SYNC_ATTEMPTS) throw error;
      continue;
    }
    return installStateSync([first, ...rest], rig);
  }
}

function applyFixturePatch(
  fixture: FixtureValues,
  descriptor: FixtureDescriptor,
  patch: FixturePatch,
): FixtureValues {
  let elements = (fixture.elements ?? []).map((element) => ({ ...element, values: { ...element.values } }));
  const byIndex = new Map(elements.map((element) => [element.index, element]));

  for (const elementPatch of patch.elements ?? []) {
    const current = byIndex.get(elementPatch.index);
    if (!current) throw new Error(`Element ${elementPatch.index} is absent from the live state.`);
    if (elementPatch.clearColorDisplay && elementPatch.colorDisplay) throw new Error('A color patch cannot set and clear at once.');
    byIndex.set(elementPatch.index, {
      ...current,
      values: applyIndexed(current.values, elementKeys(descriptor, elementPatch.index), elementPatch.channels ?? []),
      colorDisplay: elementPatch.clearColorDisplay
        ? null
        : (elementPatch.colorDisplay ?? current.colorDisplay),
    });
  }

  for (const run of patch.elementRuns ?? []) {
    if (run.first < 0 || run.count <= 0) throw new Error('Dense element run is outside the rig topology.');
    const channels = run.channels ?? [];
    const values = decodeU16(run.values, run.count * channels.length);
    const colors = run.colors ? decodeU16(run.colors, run.count * 3) : null;
    if (run.clearColorDisplay && colors) throw new Error('A color run cannot set and clear at once.');
    for (let offset = 0; offset < run.count; offset += 1) {
      const index = run.first + offset;
      const current = byIndex.get(index);
      if (!current) throw new Error(`Element ${index} is absent from the live state.`);
      const changes = channels.map((channelIndex, column) => ({
        index: channelIndex,
        value: values[offset * channels.length + column],
      }));
      const colorDisplay = run.clearColorDisplay
        ? null
        : colors
          ? { r: colors[offset * 3], g: colors[offset * 3 + 1], b: colors[offset * 3 + 2] }
          : current.colorDisplay;
      byIndex.set(index, {
        ...current,
        values: applyIndexed(current.values, elementKeys(descriptor, index), changes),
        colorDisplay,
      });
    }
  }
  elements = elements.map((element) => byIndex.get(element.index) ?? element);
  if (patch.clearColorDisplay && patch.colorDisplay) throw new Error('A color patch cannot set and clear at once.');
  return {
    ...fixture,
    values: applyIndexed(fixture.values, fixtureKeys(descriptor), patch.channels ?? []),
    colorDisplay: patch.clearColorDisplay ? null : (patch.colorDisplay ?? fixture.colorDisplay),
    elements,
  };
}

export function reduceStateDelta(current: LiveState, delta: DeltaV1, rig: RigSnapshot): ReduceResult {
  if (delta.projection !== 'delta-v1') return { kind: 'resync', reason: `Unsupported projection ${String(delta.projection)}` };
  if (delta.resyncRequired) return { kind: 'resync', reason: 'The controller requested a state refresh.' };
  if (!current.rigRevision || current.stateRevision == null) return { kind: 'resync', reason: 'No revisioned baseline is installed.' };
  if (delta.rigRevision !== current.rigRevision || (rig.rigVersionId && delta.rigRevision !== rig.rigVersionId)) {
    return { kind: 'resync', reason: 'The rig changed.' };
  }
  if (delta.stateRevision <= current.stateRevision) return { kind: 'duplicate', state: current };
  /**
   * A delta whose base is at or BEHIND our baseline is applicable. Only a base AHEAD of it is a gap.
   *
   * delta-v1 patches carry ABSOLUTE values, never increments: `applyIndexed` assigns `next[key] =
   * change.value`, `elementRuns` decode to absolute levels, and `header` is a whole replacement. So
   * applying a delta spanning `[B, E]` to a state standing at any `C` in `[B, E]` yields exactly `E` for
   * every channel the delta names. A base behind us is a SUPERSET patch to the same target, not a stale
   * one. A base ahead of us is the only shape that means something is genuinely missing — writes landed
   * between our revision and where this delta starts, and we never saw them.
   *
   * This was a strict equality, and equality is unsatisfiable on a room that is moving. The two sides
   * anchor "which version is this" to different clocks: the mirror publisher bases each delta on ITS OWN
   * last successful write, while the console's baseline comes from asking the controller what the room is
   * right now — and that round trip takes long enough that an effect running at 40 Hz has already moved
   * the room on by half a dozen revisions. The console's baseline therefore always OVERSHOOTS the
   * publisher's cursor, every delta was refused, every refusal bought another ~200 KB whole-room read,
   * and the pair never converged. That is a request loop at the mirror's 2 Hz ceiling — about 7 GB/hour
   * of Realtime Database egress, roughly $160/day, which is what one browser tab left open on a room
   * running an effect cost on 2026-09-15 and again on 2026-09-16. See `CONTRACT-console.md` B35/H14.
   *
   * RESIDUAL HAZARD, stated rather than hidden: a channel that moved between `B` and `C` and moved BACK
   * by `E` is not named in the delta at all, so this console keeps the intermediate value it holds until
   * the next delta that names that channel. The window is bounded by one publish interval (<=500 ms) and
   * it self-heals on that channel's next move. That is the price of closing this from the console alone,
   * with no controller release; resyncing onto the publisher's own cursor (asking for state AT the
   * mirror's published `panel.stateRevision`) removes it properly and needs both halves.
   */
  if (delta.baseRevision > current.stateRevision) {
    return { kind: 'resync', reason: `Expected revision ${current.stateRevision}; received ${delta.baseRevision}.` };
  }
  if (delta.ack) return { kind: 'awaiting-delta', state: current };

  try {
    const fixtures = current.fixtures.map((fixture) => ({ ...fixture, values: { ...fixture.values } }));
    for (const patch of delta.fixtures ?? []) {
      const descriptor = rig.fixtures[patch.fixtureIndex];
      if (!descriptor) throw new Error(`Fixture index ${patch.fixtureIndex} is outside the rig.`);
      const stateIndex = fixtures.findIndex((fixture) => fixture.deviceId === descriptor.deviceId);
      if (stateIndex < 0) throw new Error(`Fixture ${descriptor.deviceId} is absent from the live state.`);
      fixtures[stateIndex] = applyFixturePatch(fixtures[stateIndex], descriptor, patch);
    }
    return {
      kind: 'applied',
      state: {
        ...current,
        ...(delta.header ?? {}),
        fixtures,
        rigRevision: delta.rigRevision,
        stateRevision: delta.stateRevision,
      },
    };
  } catch (error) {
    return { kind: 'resync', reason: error instanceof Error ? error.message : 'The state delta was invalid.' };
  }
}

/**
 * Decide whether a freshly fetched full state may replace the one on screen.
 *
 * A cloud `get_state` is a slow round-trip — seconds, on a rig with a few thousand elements — and deltas keep
 * arriving at up to 2 Hz while it is in flight. Installing the answer unconditionally therefore repaints the
 * room with values that were already superseded before they arrived: the operator sees his edit, then the
 * room bouncing back to what it was, then settling once the next delta lands. That is the flicker.
 *
 * So a snapshot OLDER than what we hold is dropped. The comparison is only meaningful within one rig — a rig
 * change invalidates the baseline entirely — and an unrevisioned snapshot cannot be ordered at all, so both
 * of those adopt. This is the same ordering `reduceStateDelta` already enforces for deltas (`duplicate` on a
 * revision that does not move forward), applied to the other way state arrives.
 */
export function adoptFullState(current: LiveState | undefined, next: LiveState): LiveState {
  if (!current) return next;
  if (current.rigRevision !== next.rigRevision) return next;
  if (current.stateRevision == null || next.stateRevision == null) return next;
  return next.stateRevision < current.stateRevision ? current : next;
}

export function normalizeFullState(value: LiveState, rig?: RigSnapshot): LiveState {
  return {
    activeSource: value.activeSource ?? 'panel',
    activeSourceLabel: value.activeSourceLabel,
    blackout: value.blackout ?? false,
    outputRateHz: value.outputRateHz ?? 0,
    fixtures: value.fixtures ?? [],
    transport: value.transport ?? null,
    effects: value.effects ?? [],
    scene: value.scene ?? null,
    remoteLive: value.remoteLive,
    stream: value.stream,
    control: value.control,
    rigRevision: value.rigRevision ?? rig?.rigVersionId,
    stateRevision: value.stateRevision,
  };
}

export function makeElement(index: number, values: Record<string, number> = {}): ElementValues {
  return { index, values };
}
